import { create } from "zustand";
// TEMP / DO NOT COMMIT — devtools middleware is a local debugging aid only. Remove
// the import and unwrap the store before committing (see "Do not commit Zustand
// devtools" feedback). Lets the store be watched in Redux DevTools.
import { devtools } from "zustand/middleware";
import {
  RequestInfoField,
  RequestInfoStatus,
} from "@/lib/interfaces and Enums/ENUMRequestInformationStatus";

// LQC-637 — negotiation state for the RP/OP "Request Information" flow.
//
// This store holds ONLY the per-field request status that the trade data alone
// doesn't express (the intermediate "Requested" / "Rejected" states). The
// resolved values themselves (cover, competition, direction …) still live on the
// trade data. Both perspectives key into the same structure:
//   • RP — its single executionRequestId per esid.
//   • OP — every counterparty's executionRequestId under the esid.
//
// LATER (LQC-1745, deferred): the Requested → True/Rejected transitions are
// the natural hook for a new notification + sound. That is an add-on we come back
// to once the normal request/response flow is working — nothing in this store
// fires notifications today.

// Per execution request: the status of each of the four negotiable fields. A
// field absent from the map means it has never been requested (treated as
// False by the selectors).
export type IExecutionRequestInfoState = Partial<
  Record<RequestInfoField, RequestInfoStatus>
>;

// esid -> executionRequestId (as string) -> per-field statuses.
export type RequestInfoByESID = Record<
  string,
  Record<string, IExecutionRequestInfoState>
>;

type State = {
  requestInfo: RequestInfoByESID;
};

type Action = {
  // eslint-disable-next-line no-unused-vars
  setAllRequestInfo: (data: RequestInfoByESID) => void;
  // eslint-disable-next-line no-unused-vars
  upsertRequestInfoForESID: (
    // eslint-disable-next-line no-unused-vars
    esid: string,
    // eslint-disable-next-line no-unused-vars
    byErId: Record<string, IExecutionRequestInfoState>,
  ) => void;
  setFieldStatus: (
    // eslint-disable-next-line no-unused-vars
    esid: string,
    // eslint-disable-next-line no-unused-vars
    erId: number | string,
    // eslint-disable-next-line no-unused-vars
    field: RequestInfoField,
    // eslint-disable-next-line no-unused-vars
    status: RequestInfoStatus,
  ) => void;
};

// NOTE: devtools(...) wrapping below is TEMP — unwrap to `create<State & Action>((set) => ({...}))`
// before committing (see import note above).
const requestInformationStore = create<State & Action>()(
  devtools(
    (set) => ({
      requestInfo: {},

      // Bulk seed on first load — replaces the whole map.
      setAllRequestInfo: (data) =>
        set(() => ({ requestInfo: data }), false, "setAllRequestInfo"),

      // Real-time / single-ES refresh — replaces just this esid's ER map.
      upsertRequestInfoForESID: (esid, byErId) =>
        set(
          (state) => ({
            requestInfo: { ...state.requestInfo, [esid]: byErId },
          }),
          false,
          "upsertRequestInfoForESID",
        ),

      // Single-field write (optimistic on RP request / OP response, or reconciling
      // a push). Spreads the two nested levels so unaffected subscribers don't
      // re-render.
      setFieldStatus: (esid, erId, field, status) =>
        set(
          (state) => {
            const erKey = String(erId);
            const esidMap = state.requestInfo[esid] ?? {};
            const erMap = esidMap[erKey] ?? {};
            return {
              requestInfo: {
                ...state.requestInfo,
                [esid]: {
                  ...esidMap,
                  [erKey]: { ...erMap, [field]: status },
                },
              },
            };
          },
          false,
          "setFieldStatus",
        ),
    }),
    { name: "requestInformationStore" },
  ),
);

export default requestInformationStore;
